import React, { useEffect, useMemo, useState } from 'react';
import { PageSubheader, TicketDetailModal, CreateTicketModal } from '../shared';
import { PaymentIssuesTable } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { PaymentIssueRow } from './callCenterPaymentIssuesTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import { uiPriority, uiStatus } from '../shared/ticketAdapter';
import { Ticket } from '../../../services/ticketsApi';
import { formatInvoiceId } from '../../../utils/formatId';
import styles from './CallCenterPaymentIssues.module.css';

function adapt(t: Ticket): PaymentIssueRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    invoiceNumber: t.order_id ? formatInvoiceId(t.order_id) : '—',
    amountDisputed: '—',
    paymentType: 'Cash',
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
  };
}

const CallCenterPaymentIssues: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const loading = useTicketsStore((s) => s.loading);
  const error = useTicketsStore((s) => s.error);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const fetchList = useTicketsStore((s) => s.fetchList);

  useEffect(() => {
    setFilters({ category: 'payment' });
    fetchList();
  }, [setFilters, fetchList]);

  const rows = useMemo<PaymentIssueRow[]>(() => items.map(adapt), [items]);
  const [openTicketId, setOpenTicketId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className={styles.container}>
      <PageSubheader
        description="Invoice disputes, payment mismatches, partial payments"
        onAction={() => setShowCreate(true)}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No payment issues to display"
        onRetry={() => fetchList()}
      >
        <PaymentIssuesTable rows={rows} onView={(id) => setOpenTicketId(parseInt(id, 10))} />
      </AsyncBoundary>
      <TicketDetailModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
      <CreateTicketModal
        open={showCreate}
        defaultCategory="payment"
        lockCategory
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setOpenTicketId(id); fetchList(); }}
      />
    </div>
  );
};

export const CallCenterPaymentIssuesPage: React.FC = () => <CallCenterPaymentIssues />;

export { CallCenterPaymentIssues };
export default CallCenterPaymentIssues;
